import { useRouter } from 'next/router'
import { useQueryClient } from 'react-query'
import { useForm } from 'react-hook-form'
import { useEffect } from 'react'

import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast-v2'
import { Textarea } from '@wartek-id/textarea'

import { Header } from 'app/KinerjaPage/pages/PelaksanaanV2/components/Header'
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
} from 'app/KinerjaShared/components/Card'
import {
  API_PATH_PENGEMBANGAN_KOMPETENSI,
  useAPIGetPengembanganKompetensi,
  useAPIGetRealisasiSKP,
  useAPIPelaksanaanProgress,
  useAPISubmitPengembanganKompetensi,
} from 'api/kinerja/useAPIKinerja'
import { DEFAULT_SKP_PERIOD } from 'app/KinerjaShared/constants/period'
import { useKinerjaTipe } from 'app/KinerjaShared/hooks/useKinerjaTipe'

import type { ParamsSubmitPengembanganKompetensi } from 'types/kinerja'

type PengembanganKompetensiV2Props = { isReadOnly?: boolean; year?: number }

export function PengembanganKompetensiV2({
  isReadOnly = false,
  year,
}: PengembanganKompetensiV2Props) {
  const router = useRouter()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { tipe } = useKinerjaTipe()

  // Override period params only if the year param exist
  const hasYearParam = !!year
  const periodParams = hasYearParam
    ? {
        // Use period=1 for the new document flow
        skp_period: DEFAULT_SKP_PERIOD,
        skp_year: year,
      }
    : undefined

  const formMethods = useForm<
    Pick<
      ParamsSubmitPengembanganKompetensi,
      'reflectionImplementation' | 'reflectionInspiration'
    >
  >({
    mode: 'onChange',
    defaultValues: {
      reflectionInspiration: '',
      reflectionImplementation: '',
    },
  })

  // Get pelaksanaan progress data
  const { data: dataPelaksanaan, isLoading: isLoadingPelaksanaanProgress } =
    useAPIPelaksanaanProgress({
      periodParams,
      opts: {
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })

  // Get indikator data
  const { data: dataIndikator, isLoading: isLoadingIndikator } =
    useAPIGetRealisasiSKP({
      params: {
        kategori: ['PK'],
        periodParams,
      },
      options: {
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })

  // Get pengembangan kompetensi data
  const {
    data: dataPengembanganKompetensi,
    isLoading: isLoadingPengembanganKompetensi,
  } = useAPIGetPengembanganKompetensi({
    params: {
      tipe,
      periodParams,
    },
    options: {
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  // Sync pengembangan kompetensi data
  useEffect(() => {
    if (!!dataPengembanganKompetensi) {
      // Set data from BE as initial form data
      formMethods.reset(dataPengembanganKompetensi?.data?.competencyImprovement)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataPengembanganKompetensi])

  const submitMutation = useAPISubmitPengembanganKompetensi()
  const draftMutation = useAPISubmitPengembanganKompetensi()

  const isLoading =
    isLoadingPengembanganKompetensi || isLoadingPelaksanaanProgress

  // In the new documents flow (>=2025), user will only select one Pengembangan Kompetensi
  // via Perencanaan, so that we can safely display the first item
  const indikator = dataIndikator?.data?.[0]?.deskripsi

  const state = dataPengembanganKompetensi?.data?.state
  const isSubmitted = state === 'SUBMITTED' || state === 'APPROVED'
  const isEvaluated = dataPelaksanaan?.data?.evaluationDocumentState === 'READY'
  const isEvaluatedBeforeSubmitted = isEvaluated && !isSubmitted

  // Only show action buttons if the SKP is not evaluated
  // and the form is neither submitted nor approved.
  const hasActions = !isEvaluated && !isSubmitted && !isLoading && !isReadOnly

  const isFormDisabled = isReadOnly || isEvaluatedBeforeSubmitted
  const isSubmitDisabled = !formMethods.formState.isValid
  const isDraftDisabled = !formMethods.formState.isDirty

  const handleSaveDraft = () => {
    if (isEvaluated || isSubmitted || isDraftDisabled) return

    draftMutation.mutate(
      {
        state: 'DRAFT',
        ...formMethods.getValues(),
        tipe,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(API_PATH_PENGEMBANGAN_KOMPETENSI)
          toast({
            message: 'Berhasil simpan draf!',
            type: 'success',
          })
        },
        onError: () => {
          toast({
            message: 'Gagal simpan draf. Coba lagi.',
          })
        },
      }
    )
  }

  const handleSubmit = () => {
    if (isEvaluated || isSubmitted || isSubmitDisabled) return

    submitMutation.mutate(
      {
        state: 'SUBMITTED',
        ...formMethods.getValues(),
        tipe,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(API_PATH_PENGEMBANGAN_KOMPETENSI)
          toast({
            message: 'Berhasil kirim ke atasan!',
            type: 'success',
          })
        },
        onError: () => {
          toast({
            message: 'Gagal kirim ke atasan. Coba lagi.',
          })
        },
      }
    )
  }

  return (
    <div className="px-8 pb-8 pt-12 bg-gray-10 min-h-full">
      <div className="max-w-7xl mx-auto">
        <Header
          title="Pengembangan Kompetensi"
          className="mb-9 print:hidden"
          onBack={() => {
            router.back()
          }}
        />

        <div className="flex items-start gap-8">
          <div className="w-96 flex-shrink-0 flex-grow-0 space-y-4">
            <Card withBorder aria-live="polite" aria-busy={isLoadingIndikator}>
              <CardBody className="p-4 space-y-2">
                <Text as="h2" variant="heading-sm">
                  Indikator pilihan Anda
                </Text>
                <hr className="border-black-20" />
                {isLoadingIndikator ? (
                  <>
                    <Shimmer className="!w-full" />
                    <Shimmer className="!w-full" />
                    <Shimmer />
                  </>
                ) : (
                  <Text color="subdued">{indikator || '-'}</Text>
                )}
              </CardBody>
            </Card>

            <aside className="p-4 flex gap-2 rounded-lg border border-gray-50">
              <Icon fontSize="sm" className="!text-xl !leading-5">
                info
              </Icon>

              <ul className="list-disc pl-6 space-y-2">
                <Text as="li">
                  <strong>Lakukan kegiatan</strong> yang berkaitan dengan
                  indikator pilihan Anda sebelum mengisi refleksi.
                </Text>
                <Text as="li">
                  <strong>Tidak perlu kumpulkan Bukti Dukung</strong> untuk
                  Pengembangan Kompetensi.
                </Text>
              </ul>
            </aside>
          </div>

          <Card
            aria-busy={isLoading}
            aria-live="polite"
            className="w-full"
            withBorder
          >
            <CardHeader className="space-y-1">
              {isLoading ? (
                <Shimmer height="32px" width="100%" />
              ) : isReadOnly ? (
                <Text variant="heading-lg" as="h2">
                  Dokumen Refleksi
                </Text>
              ) : isEvaluatedBeforeSubmitted ? (
                <div className="flex items-center space-x-2">
                  <Icon color="informational">info</Icon>

                  <Text variant="heading-sm" as="h2">
                    Dokumen Refleksi tidak dapat diubah lagi karena Predikat
                    Kinerja sudah ditetapkan
                  </Text>
                </div>
              ) : isSubmitted ? (
                <div className="flex items-center space-x-2">
                  <Icon color="success">check_circle</Icon>

                  <Text variant="heading-sm" as="h2">
                    Dokumen Refleksi sudah dikirim ke Kepala Sekolah
                  </Text>
                </div>
              ) : (
                <>
                  <Text variant="heading-lg" as="h2">
                    Isi Refleksi
                  </Text>
                  <Text>
                    Anda dapat ubah pengisian secara berkala selama Penetapan
                    Predikat Kinerja belum dilakukan.
                  </Text>
                </>
              )}
            </CardHeader>

            {isLoading ? (
              <CardBody className="p-6 space-y-4">
                <Shimmer height={112} width="100%" />
                <Shimmer height={112} width="100%" />
              </CardBody>
            ) : isSubmitted || isReadOnly ? (
              <CardBody className="p-6">
                <dl className="space-y-6">
                  <div className="space-y-2">
                    <Text variant="heading-sm" as="dt">
                      Apa inspirasi menarik yang Anda peroleh dari kegiatan?
                    </Text>
                    <Text as="dd" className="overflow-wrap-anywhere">
                      {formMethods.getValues('reflectionInspiration') || '-'}
                    </Text>
                  </div>

                  <div className="space-y-2">
                    <Text variant="heading-sm" as="dt">
                      Bagaimana Anda menerapkan inspirasi tersebut untuk
                      kemajuan penguasaan kompetensi?
                    </Text>
                    <Text as="dd" className="overflow-wrap-anywhere">
                      {formMethods.getValues('reflectionImplementation') || '-'}
                    </Text>
                  </div>
                </dl>
              </CardBody>
            ) : (
              <CardBody className="p-6 space-y-4">
                <fieldset className="space-y-2">
                  <div className="space-y-1">
                    <Text variant="heading-sm" as="label" htmlFor="inspiration">
                      Apa inspirasi menarik yang Anda peroleh dari kegiatan?
                    </Text>
                    <Text id="inspirationDesc">
                      Ketik maksimal 3 inspirasi:
                    </Text>
                  </div>
                  <Textarea
                    aria-describedby="inspirationDesc"
                    className="h-20 !resize-y"
                    id="inspiration"
                    isDisabled={isFormDisabled}
                    maxLength={1000}
                    placeholder="Ketik di sini"
                    {...formMethods.register('reflectionInspiration', {
                      required: true,
                    })}
                  />
                </fieldset>

                <fieldset className="space-y-2">
                  <div className="space-y-1">
                    <Text
                      variant="heading-sm"
                      as="label"
                      htmlFor="implementaion"
                    >
                      Bagaimana Anda menerapkan inspirasi tersebut untuk
                      kemajuan penguasaan kompetensi?
                    </Text>
                    <Text id="implementationDesc">
                      Ketik minimal 1 penerapan untuk kemajuan penguasaan
                      kompetensi Anda:
                    </Text>
                  </div>

                  <Textarea
                    aria-describedby="implementationDesc"
                    className="h-20 !resize-y"
                    id="implementaion"
                    isDisabled={isFormDisabled}
                    maxLength={1000}
                    placeholder="Ketik di sini"
                    {...formMethods.register('reflectionImplementation', {
                      required: true,
                    })}
                  />
                </fieldset>
              </CardBody>
            )}

            {hasActions && (
              <CardFooter
                withSeparator={false}
                className="pt-0 flex justify-end space-x-2"
              >
                <Button
                  size="lg"
                  color="white"
                  isLoading={isLoading}
                  disabled={
                    isFormDisabled ||
                    isDraftDisabled ||
                    draftMutation.isLoading ||
                    !formMethods.formState.isDirty
                  }
                  onClick={handleSaveDraft}
                >
                  {draftMutation.isLoading ? 'Menyimpan...' : 'Simpan draf'}
                </Button>
                <Button
                  size="lg"
                  isLoading={isLoading}
                  disabled={
                    isFormDisabled ||
                    isSubmitDisabled ||
                    submitMutation.isLoading
                  }
                  onClick={handleSubmit}
                >
                  {submitMutation.isLoading ? 'Mengirim...' : 'Kirim'}
                </Button>
              </CardFooter>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}
